import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { OrbitControls as OrbitControlsType } from "three-stdlib/controls/OrbitControls";

import { OrbitControls } from "@react-three/drei";

import { useFrame, useThree } from "@react-three/fiber";
import { Vector3, Matrix4 } from "three";

import TWEEN from "@tweenjs/tween.js";

import { CAMERA, RADIUS, RENDER_ORDER } from "@graphic/constants";
import { useInteractObjectStore, useTimeControlStore } from "@graphic/stores";
import { getPositionAroundFocusTarget } from "@graphic/utils";
import { CameraType } from "@graphic/types";

type TweenValuesType = {
  targetFrom: Vector3;
  targetTo: Vector3;
  positionFrom: Vector3;
  positionTo: Vector3;
};

function ControlSetup({ cameraType }: { cameraType: CameraType }) {
  const get = useThree(state => state.get);
  const focus = useInteractObjectStore(state => state.focus);

  const { play, setPlay, setSpeed } = useTimeControlStore();

  const [
    focusTarget,
    center,
    targetFrom,
    targetTo,
    positionFrom,
    positionTo,
    objectToCameraDirection
  ] = useMemo(() => {
    return Array.from({
      length: 7
    }).map(() => new Vector3(0, 0, 0));
  }, []);

  const matrix4 = useMemo(() => new Matrix4(), []);

  const focusRef = useRef<THREE.Group | null>(null);
  const controlRef = useRef<OrbitControlsType>(null);

  const focusIdRef = useRef<string | null>(null);

  const playRef = useRef<boolean>(false);
  const disableRef = useRef<boolean>(false);

  const panRef = useRef<boolean>(true);

  const disable = cameraType === "ORTHOGRAPHIC";

  useFrame(() => {
    TWEEN.update(performance.now());
  }, RENDER_ORDER.CONTROL);

  useLayoutEffect(() => {
    if (!controlRef.current) return;

    if (focus.object === null && !focusIdRef.current) return;

    if (focus.object === null) {
      focusIdRef.current = null;
    } else {
      const { uuid } = focus.object;
      const scene = get().scene;
      focusRef.current = scene.getObjectByProperty("uuid", uuid) as THREE.Group;
      if (!focusRef.current) return;
      focusIdRef.current = uuid;
    }

    playRef.current = play;
    setPlay(false);
    setSpeed(1);

    disableRef.current = true;

    const targetTween = getTargetTween();

    targetTween.start();
  }, [focus]);

  useFrame(() => {
    if (!controlRef.current) return;
    if (!focusRef.current || !focusIdRef.current) return;

    if (disableRef.current) return;
    if (panRef.current) return;

    // const focusId = focusIdRef.current;

    const targetPosition = focusRef.current.position;

    focusTarget.set(targetPosition.x, targetPosition.y, targetPosition.z);
    // controlRef.current.object.position.copy(focusTarget).add(objectToCameraDirection);
    controlRef.current.target.set(focusTarget.x, focusTarget.y, focusTarget.z);

    const camera = controlRef.current.object as THREE.PerspectiveCamera;
    camera.position.copy(focusTarget).add(objectToCameraDirection);

    camera.updateProjectionMatrix();

    controlRef.current.update();
  }, RENDER_ORDER.CAMERA);

  useFrame(() => {
    if (!controlRef.current) return;
    if (!focusRef.current || !focusIdRef.current) return;

    if (disableRef.current) return;

    const targetPosition = focusRef.current.position;

    focusTarget.set(targetPosition.x, targetPosition.y, targetPosition.z);

    const camera = controlRef.current.object as THREE.PerspectiveCamera;

    objectToCameraDirection.copy(camera.position);
    objectToCameraDirection.sub(focusTarget);
  }, RENDER_ORDER.BEFORE);

  const getTargetTween = () => {
    const tweenValues = getTweenValues();
    const tween = makeTween(tweenValues);

    return tween;
  };

  const getTweenValues = () => {
    const camera = controlRef.current.object as THREE.PerspectiveCamera;
    const target = controlRef.current.target as Vector3;

    targetFrom.copy(target);
    positionFrom.copy(camera.position);

    if (focus.object === null) {
      focusRef.current = null;
      targetTo.copy(center);
      positionTo.copy(positionFrom).setLength(CAMERA.PERSPECTIVE.DISTANCE);
    } else {
      const { object, type } = focus;
      const { uuid } = object;
      const distanceFromTarget = type === "Default" ? 1000 : 6000;

      const scene = get().scene;
      focusRef.current = scene.getObjectByProperty("uuid", uuid) as THREE.Group;

      const focusMatrix = focusRef.current?.matrixWorld;

      const lookAtVector = getPositionAroundFocusTarget(
        focusMatrix,
        distanceFromTarget
      );

      targetTo.setFromMatrixPosition(focusMatrix);
      positionTo.copy(targetTo).add(lookAtVector);
    }

    return { targetFrom, targetTo, positionFrom, positionTo };
  };

  const makeTween = ({
    targetFrom,
    targetTo,
    positionFrom,
    positionTo
  }: TweenValuesType) => {
    const camera = controlRef.current.object as THREE.PerspectiveCamera;
    const target = controlRef.current.target as Vector3;

    const cameraUpFrom = camera.up.clone();
    const cameraUpTo = focus.object
      ? targetTo.clone().normalize()
      : new Vector3(0, 0, 1);

    const targetTween = new TWEEN.Tween(targetFrom)
      .to(targetTo, 2500)
      .easing(TWEEN.Easing.Quadratic.Out)
      .onUpdate((object, elapsed) => {
        const position = positionFrom.clone().lerp(positionTo, elapsed);
        const { x, y, z } = position;

        camera.position.set(x, y, z);

        const up = cameraUpFrom.clone().lerp(cameraUpTo, elapsed);

        camera.up.set(up.x, up.y, up.z);

        camera.updateProjectionMatrix();

        target.set(targetFrom.x, targetFrom.y, targetFrom.z);
        controlRef.current.update();
      })
      .onStart(() => {
        // disableRef.current = true;
      })
      .onComplete(() => {
        focusTarget.copy(targetTo);
        target.set(targetTo.x, targetTo.y, targetTo.z);
        controlRef.current.update();

        if (focus.object === null) {
          // focusRef.current = null;
          focusIdRef.current = null;
        } else {
          const { uuid } = focus.object;
          focusIdRef.current = uuid;
        }
        disableRef.current = false;
        setPlay(playRef.current);
        panRef.current = true;
      });

    return targetTween;
  };

  return (
    <OrbitControls
      makeDefault
      ref={controlRef}
      zoomSpeed={0.8}
      enableDamping={false}
      enableZoom={disable ? false : !disableRef.current}
      enableRotate={disable ? false : !disableRef.current}
      enablePan={disable ? false : !disableRef.current}
      panSpeed={cameraType === "PERSPECTIVE" ? 50 : 1}
      // minZoom={CAMERA.ZOOM_MIN}
      // maxZoom={CAMERA.ZOOM_MAX}
      // minDistance={CAMERA.DISTANCE_MIN}
      // maxDistance={CAMERA.DISTANCE_MAX}
    />
  );
}

export default ControlSetup;
